import React, { useState, useRef, useEffect } from "react";
import userIcon from "../../../images/icons/user.png";
import phoneIcon from "../../../images/icons/mobile.png";
import branchIcon from "../../../images/icons/branch.png";
import emailIcon from "../../../images/icons/mail.png";
import { useTheme, themes, type ThemeName } from "../../../contexts/ThemeContext";
import { useTour } from "../../../contexts/TourContext";
import { useAppDispatch } from "../../../state/hooks";
import { showNotification } from "../../../state/slices/notificationsSlice";
import { authService } from "../../../services/auth";
import { executeConfigAction } from "../../../state/thunks/configThunks";

interface UserInfo {
  name: string;
  id: string;
  email: string;
  role: string;
  phone: string;
  branch: string;
  avatar?: string;
}

interface ProfileDropdownProps {
  userInfo: UserInfo;
  onLogout: () => void;
}

export const ProfileDropdown: React.FC<ProfileDropdownProps> = ({
  userInfo,
  onLogout,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isTriggerHovered, setIsTriggerHovered] = useState(false);
  const [isLogoutHovered, setIsLogoutHovered] = useState(false);
  const [isTourHovered, setIsTourHovered] = useState(false);
  const [isResetPasswordHovered, setIsResetPasswordHovered] = useState(false);
  const [isResettingPassword, setIsResettingPassword] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { currentTheme, changeTheme } = useTheme();
  const { startTour } = useTour();
  const dispatch = useAppDispatch();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleLogout = () => {
    setIsOpen(false);
    onLogout();
  };

  const handleStartTour = () => {
    setIsOpen(false);
    // Small delay to allow dropdown to close before tour starts
    setTimeout(() => {
      startTour();
    }, 150);
  };

  const handleResetPassword = async () => {
    // Get userid from localStorage (from encryptedUserData)
    let userId: string | null = null;
    
    try {
      const encryptedUserData = localStorage.getItem("encryptedUserData");
      if (encryptedUserData) {
        const decryptedData = authService.decryptData(encryptedUserData);
        if (decryptedData) {
          userId = decryptedData.userid || decryptedData.usercode || userInfo?.id || null;
        }
      }
    } catch (error) {
      console.warn("Failed to get userid from localStorage:", error);
    }

    // Fallback to userInfo.id if not found in localStorage
    if (!userId && userInfo?.id) {
      userId = userInfo.id;
    }

    if (!userId) {
      dispatch(
        showNotification({
          message: "User ID not found. Cannot reset password.",
          type: "error",
        })
      );
      return;
    }

    setIsResettingPassword(true);
    setIsOpen(false);

    try {
      // Use executeConfigAction to trigger the edit flow
      // This will:
      // 1. Load screen config: /pabws/screen/users_add
      // 2. Load user data: /pabws/module/users_add/{userid}
      const editAction = {
        Action: {
          Action: "Navigate",
          Navigate: `modulev1/users_add/${userId}`,
        },
        ActionType: "Edit",
        ScreenId: "42",
      };

      const actionPayload = {
        id: userId,
        __summaryAction: editAction,
      };

      // This will trigger the same flow as clicking Edit from summary screen
      dispatch(executeConfigAction("edit", actionPayload));
      
      dispatch(
        showNotification({
          message: "Navigating to edit screen to reset password...",
          type: "info",
        })
      );
    } catch (error) {
      console.error("Reset password navigation error:", error);
      dispatch(
        showNotification({
          message: "An error occurred while navigating to edit screen. Please try again.",
          type: "error",
        })
      );
    } finally {
      setIsResettingPassword(false);
    }
  };

  return (
    <div className="relative inline-block" ref={dropdownRef} data-tour="profile">
      {/* Trigger Button */}
      <div
        className={`flex items-center gap-2 py-2 px-3 rounded-lg bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 cursor-pointer transition-all duration-200 text-sm font-medium text-gray-700 dark:text-gray-200 ${
          isTriggerHovered ? "bg-gray-100 dark:bg-gray-600 border-gray-300 dark:border-gray-500" : ""
        }`}
        onClick={() => setIsOpen(!isOpen)}
        onMouseEnter={() => setIsTriggerHovered(true)}
        onMouseLeave={() => setIsTriggerHovered(false)}
      >
        <div className="w-[26px] h-[26px] rounded-full bg-primary-500 flex items-center justify-center text-white text-sm font-semibold shrink-0">
          {userInfo.avatar || userInfo.name.charAt(0).toUpperCase()}
        </div>
        <span>{userInfo.name}</span>
        <span
          className={`text-xs text-gray-500 transition-transform duration-200 ${
            isOpen ? "rotate-180" : "rotate-0"
          }`}
        >
          ▼
        </span>
      </div>

      {/* Dropdown Content */}
      <div
        className={`absolute top-full right-0 mt-2 w-[300px] bg-white dark:!bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-[1000] overflow-hidden transition-all duration-200 ${
          isOpen
            ? "translate-y-0 scale-100 opacity-100 visible"
            : "-translate-y-2 scale-95 opacity-0 invisible"
        }`}
      >
        {/* Header Section */}
        <div className="p-4 text-center bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
          <div className="w-15 h-15 rounded-full bg-primary-500 flex items-center justify-center text-white text-2xl font-bold mx-auto mb-3">
            {userInfo.avatar || userInfo.name.charAt(0).toUpperCase()}
          </div>
          <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 m-0 mb-1">
            {userInfo.name}
          </h3>
         {/* <p className="text-sm text-gray-500 dark:text-gray-400 m-0">{userInfo.id}</p> */}
        </div>

        {/* Content Section */}
        <div className="p-4 max-h-[400px] overflow-y-auto">
          {/* Email */}
          <div className="flex items-center gap-3 py-2 text-sm text-gray-700 dark:text-gray-200">
            <span className="w-4 h-4 text-base text-gray-500 dark:text-gray-400 text-center shrink-0 flex items-center justify-center">
              <img src={emailIcon} alt="email" className="dark:brightness-0 dark:invert dark:opacity-90 w-4 h-4" />
            </span>
            <span>{userInfo.email}</span>
          </div>

          {/* Role */}
          <div className="flex items-center gap-3 py-2 text-sm text-gray-700 dark:text-gray-200">
            <span className="w-4 h-4 text-base text-gray-500 dark:text-gray-400 text-center shrink-0 flex items-center justify-center">
              <img src={userIcon} alt="user" className="dark:brightness-0 dark:invert dark:opacity-90 w-4 h-4" />
            </span>
            <span>{userInfo.role}</span>
          </div>

          {/* Phone */}
          <div className="flex items-center gap-3 py-2 text-sm text-gray-700 dark:text-gray-200">
            <span className="w-4 h-4 text-base text-gray-500 dark:text-gray-400 text-center shrink-0 flex items-center justify-center">
              <img src={phoneIcon} alt="phone" className="dark:brightness-0 dark:invert dark:opacity-90 w-4 h-4" />
            </span>
            <span>{userInfo.phone}</span>
          </div>

          {/* Branch */}
          <div className="flex items-center gap-3 py-2 text-sm text-gray-700 dark:text-gray-200">
            <span className="w-4 h-4 text-base text-gray-500 dark:text-gray-400 text-center shrink-0 flex items-center justify-center">
              <img src={branchIcon} alt="branch" className="dark:brightness-0 dark:invert dark:opacity-90 w-4 h-4" />
            </span>
            <span>{userInfo.branch}</span>
          </div>

          {/* Theme Switcher */}
          <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-600">
            <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3 px-1">
              Theme Color
            </div>
            <div className="flex items-center justify-between gap-2">
              {(["green", "blue", "purple", "orange"] as ThemeName[]).map((themeName) => {
                const theme = themes[themeName];
                const isActive = currentTheme === themeName;
                return (
                  <button
                    key={themeName}
                    onClick={() => changeTheme(themeName)}
                    className={`w-8 h-8 rounded-full transition-all duration-200 hover:scale-110 ${
                      isActive
                        ? "ring-2 ring-offset-2 ring-primary-500 dark:ring-primary-400 scale-110"
                        : "ring-2 ring-transparent hover:ring-primary-300 dark:hover:ring-primary-600"
                    }`}
                    style={{ backgroundColor: theme.color }}
                    title={theme.name}
                    aria-label={`Switch to ${theme.name} theme`}
                  />
                );
              })}
            </div>
          </div>

          {/* Reset Password Button */}
          <button
            className={`w-full p-3 bg-yellow-50 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300 border border-yellow-200 dark:border-yellow-800 rounded-lg text-sm font-semibold cursor-pointer transition-all duration-200 mt-4 hover:bg-yellow-100 dark:hover:bg-yellow-900/50 flex items-center justify-center gap-2 ${
              isResetPasswordHovered ? "bg-yellow-100 dark:bg-yellow-900/50" : ""
            } ${isResettingPassword ? "opacity-70 cursor-not-allowed" : ""}`}
            onClick={handleResetPassword}
            disabled={isResettingPassword}
            onMouseEnter={() => setIsResetPasswordHovered(true)}
            onMouseLeave={() => setIsResetPasswordHovered(false)}
            title="Reset your password and go to edit screen"
          >
            {isResettingPassword ? (
              <>
                <svg
                  className="w-5 h-5 animate-spin"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                Resetting...
              </>
            ) : (
              <>
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z"
                  />
                </svg>
                Reset Password
              </>
            )}
          </button>

          {/* App Tour Button */}
          <button
            className={`w-full p-3 bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 border border-primary-200 dark:border-primary-800 rounded-lg text-sm font-semibold cursor-pointer transition-all duration-200 mt-3 hover:bg-primary-100 dark:hover:bg-primary-900/50 flex items-center justify-center gap-2 ${
              isTourHovered ? "bg-primary-100 dark:bg-primary-900/50" : ""
            }`}
            onClick={handleStartTour}
            onMouseEnter={() => setIsTourHovered(true)}
            onMouseLeave={() => setIsTourHovered(false)}
            title="Take a guided tour of the application"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
              />
            </svg>
            Start App Tour
          </button>

          {/* Logout Button */}
          <button
            className={`w-full p-3 bg-gray-800 dark:bg-gray-700 text-white border-none rounded-lg text-sm font-semibold cursor-pointer transition-all duration-200 mt-3 hover:bg-gray-900 dark:hover:bg-gray-600 ${
              isLogoutHovered ? "bg-gray-900 dark:bg-gray-600" : ""
            }`}
            onClick={handleLogout}
            onMouseEnter={() => setIsLogoutHovered(true)}
            onMouseLeave={() => setIsLogoutHovered(false)}
          >
            Logout
          </button>
        </div>
      </div>
    </div>
  );
};
